import { useCallback, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { useRepairOrders } from "../../context/RepairOrdersContext";
import {
  formatCurrency,
  formatDateDisplay,
  formatMileage,
  statusColorMap,
} from "../useRepairOrdersPage/formatters";

const DEFAULT_LIMIT = 5;

const buildOverviewOrder = (order) => {
  const mileage = formatMileage(order.mileage ?? order.vehicle?.serviceMileage);
  const advisor = order.advisorName || "—";
  const appointment = formatDateDisplay(order.appointmentDate, "MMM DD YYYY · hh:mm A");

  return {
    id: order.serviceOrderUuid,
    vehicle:
      order.vehicle?.year && order.vehicle?.make && order.vehicle?.model
        ? `${order.vehicle.year} ${order.vehicle.make} ${order.vehicle.model}`
        : order.vehicle?.model || order.vehicle?.make || "—",
    roNumber: order.repairOrderNumber || "—",
    mileageIn: mileage || "—",
    status: {
      text: order.statusDescription || order.statusCode || "—",
      color: statusColorMap[order.statusCode] || "neutral",
    },
    details: [
      {
        label: "Date and Time",
        value: appointment || "—",
      },
      {
        label: "Service Mileage",
        value: mileage || "—",
      },
      {
        label: "Advisor",
        value: advisor,
      },
      {
        label: "Total Amount",
        value: formatCurrency(order.totalAmount) || "—",
      },
    ],
  };
};

export const useRepairOrdersSnapshot = (limit = DEFAULT_LIMIT) => {
  const { contactId } = useParams();
  const {
    baseOrders,
    baseMeta,
    basePartyUuid,
    loading,
    error,
    isEmpty,
    fetchRepairOrders,
    setFilteredOrders,
    resetRepairOrders,
  } = useRepairOrders();
  const snapshotLoading = loading && !baseOrders.length;

  const hasAttemptedLoadRef = useRef(false);
  useEffect(() => {
    if (
      contactId &&
      basePartyUuid &&
      basePartyUuid !== contactId
    ) {
      resetRepairOrders();
    }

    hasAttemptedLoadRef.current = false;
  }, [contactId, basePartyUuid, resetRepairOrders]);

  const loadBase = useCallback(() => {
    if (!contactId) return;
    hasAttemptedLoadRef.current = true;
    fetchRepairOrders(
      {
        party_uuid: contactId,
        page: 1,
        page_size: 20,
        order_by: ["createdAt"],
        order_direction: ["DESC"],
      },
      { persistAsBase: true }
    );
  }, [contactId, fetchRepairOrders]);

  useEffect(() => {
    if (!contactId) return;

    if (baseOrders.length) {
      hasAttemptedLoadRef.current = true;
      setFilteredOrders(baseOrders, baseMeta);
      return;
    }

    if (hasAttemptedLoadRef.current) {
      return;
    }

    loadBase();
  }, [
    contactId,
    baseOrders,
    baseMeta,
    loadBase,
    setFilteredOrders,
  ]);

  const snapshotOrders = useMemo(
    () => baseOrders.slice(0, limit).map(buildOverviewOrder),
    [baseOrders, limit]
  );

  return {
    contactId,
    loading: snapshotLoading,
    error,
    isEmpty,
    meta: baseMeta,
    orders: snapshotOrders,
    refetch: loadBase,
  };
};

